// Contacts + contact-groups client hooks.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface ContactGroupRef {
  id: number;
  name: string;
  color: string;
}
export interface Contact {
  email: string;
  name: string;
  company: string;
  timesSeen: number;
  lastSeen: string;
  groups: ContactGroupRef[];
}
export interface ContactGroup {
  id: number;
  name: string;
  color: string;
  count: number;
}

async function jget<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: 'include' });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json() as Promise<T>;
}
async function jsend(url: string, method: string, body?: unknown): Promise<Response> {
  const r = await fetch(url, {
    method,
    credentials: 'include',
    headers: body ? { 'content-type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r;
}

export function useContacts(opts: { query?: string; groupId?: number | null }) {
  const q = (opts.query ?? '').trim();
  const gid = opts.groupId ?? null;
  return useQuery<{ contacts: Contact[] }>({
    queryKey: ['contacts', q, gid],
    queryFn: () => {
      const p = new URLSearchParams();
      if (q) p.set('query', q);
      if (gid != null) p.set('groupId', String(gid));
      const qs = p.toString();
      return jget(`/api/contacts${qs ? `?${qs}` : ''}`);
    },
  });
}

export function useContactGroups() {
  return useQuery<{ groups: ContactGroup[] }>({
    queryKey: ['contact-groups'],
    queryFn: () => jget('/api/contact-groups'),
  });
}

function useInvalidateContacts() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['contacts'] });
    void qc.invalidateQueries({ queryKey: ['contact-groups'] });
  };
}

export function useCreateGroup() {
  const inval = useInvalidateContacts();
  return useMutation({
    mutationFn: (v: { name: string; color?: string }) =>
      jsend('/api/contact-groups', 'POST', v).then((r) => r.json()),
    onSuccess: inval,
  });
}

export function useUpdateGroup() {
  const inval = useInvalidateContacts();
  return useMutation({
    mutationFn: (v: { id: number; name?: string; color?: string }) =>
      jsend(`/api/contact-groups/${v.id}`, 'PATCH', { name: v.name, color: v.color }),
    onSuccess: inval,
  });
}

export function useDeleteGroup() {
  const inval = useInvalidateContacts();
  return useMutation({
    mutationFn: (id: number) => jsend(`/api/contact-groups/${id}`, 'DELETE'),
    onSuccess: inval,
  });
}

export function useAddToGroup() {
  const inval = useInvalidateContacts();
  return useMutation({
    mutationFn: (v: { groupId: number; emails: string[] }) =>
      jsend(`/api/contact-groups/${v.groupId}/members`, 'POST', { emails: v.emails }),
    onSuccess: inval,
  });
}

export function useRemoveFromGroup() {
  const inval = useInvalidateContacts();
  return useMutation({
    mutationFn: (v: { groupId: number; email: string }) =>
      jsend(`/api/contact-groups/${v.groupId}/members`, 'DELETE', { email: v.email }),
    onSuccess: inval,
  });
}

export function useAddContact() {
  const inval = useInvalidateContacts();
  return useMutation({
    mutationFn: (v: { email: string; name?: string }) =>
      jsend('/api/contacts', 'POST', v).then((r) => r.json()),
    onSuccess: inval,
  });
}
